import { useState } from 'react'
import { useNavigate, useSearch, Link } from '@tanstack/react-router'
import { TopBarBackIconButton } from './topbar'
import { useBackTo } from './topbar/useBackTo'
import { AppLayout } from './AppLayout'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'
import {
  ArrowLeft,
  CalendarDays,
  Crown,
  CupSoda,
  MessageSquare,
  Send,
  Soup,
  Stethoscope,
  TriangleAlert,
} from '@/components/icons'
import { hapticLight } from '@/lib/haptics'
import { formatCrewRole } from '@/lib/utils'
import { CrewFlightStats } from './CrewFlightStats'
import dayjs from 'dayjs'
import { Card } from '@/components/ui/card'
import { PrntrGroup, PrntrInput, PrntrValue } from '@/components/ui/prntr-input'
import { Button } from './ui/button'
import { ProfileAvatar } from './ProfileAvatar'
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs'
import { useCrewGetById, useCrewResendInvite } from '@/lib/hooks'
import { Skeleton } from './ui/skeleton'
import { toast } from 'sonner'
import { documentSubTypeLabels, isPilotRole } from '@@api/shared'
import { Badge } from '@/components/ui/badge'
import { AlertBadge } from '@/components/ui/alert-badge'
import { CrewActionsSelect } from './CrewActionsSelect'
import { DocumentFormDrawer } from './documents/DocumentFormDrawer'
import { ViewDocumentDrawer } from './documents/ViewDocumentDrawer'
import { FlightList } from './FlightList'
import { EmptyStateCard } from './EmptyStateCard'
import { PreferenceCard } from './PAXDetailView'
import { PhoneLink, EmailLink } from './ContactLink'
import { CrewPreferencePhotos } from './CrewPreferencePhotos'
import { useIsAdmin } from '@/hooks/useIsCompanyAdmin'
import { useDocumentGetByCrew } from '@/lib/hooks/document'
import {
  DocumentListCard,
  getDocumentExpiryStatus,
  getDocumentIcon,
} from './DocumentListCard'
import { useFindOrCreateConversation } from '@/hooks/useConvexMessaging'
import { useCurrentCrewMember } from '@/hooks/useCurrentCrewMember'
import { getAirportByIcao } from '@/lib/airport-db'

function YesNoBadge({ value }: { value: boolean }) {
  return (
    <Badge
      variant="outline"
      className={`rounded-lg text-xs font-semibold ${
        value ? 'text-content-positive-bold' : 'text-content-primary-subtle'
      }`}
    >
      {value ? 'Yes' : 'No'}
    </Badge>
  )
}

// Helper function to get the expiry status of documents
function getDocumentsExpiryStatus(
  documents: { expiryDate: Date }[] | undefined
): 'expired' | 'expiring' | 'valid' {
  if (!documents || documents.length === 0) return 'valid'
  let hasExpired = false
  let hasExpiring = false
  for (const doc of documents) {
    const status = getDocumentExpiryStatus(doc.expiryDate)
    if (status === 'expired') {
      hasExpired = true
      break
    }
    if (status === 'expiring') {
      hasExpiring = true
    }
  }
  if (hasExpired) return 'expired'
  if (hasExpiring) return 'expiring'
  return 'valid'
}

export interface CrewDetailViewProps {
  crewId: string
  initialTab?: 'overview' | 'documents' | 'flights'
  initialDocumentId?: string
  publicView?: boolean
  /** List route the breadcrumb/back returns to — set by the rendering route. */
  backTo?: '/crew-directory' | '/operations/crew-directory'
}
export function CrewDetailView({
  crewId,
  initialTab = 'overview',
  initialDocumentId,
  publicView = false,
  backTo = '/crew-directory',
}: CrewDetailViewProps) {
  const navigate = useNavigate()
  const handleBack = useBackTo(backTo)
  const { data: crew, isLoading, refetch } = useCrewGetById(crewId)
  // Server-filtered: non-admins only receive public (or their own) documents.
  const { data: crewDocs } = useDocumentGetByCrew(crewId)
  const [isAddDocumentDrawerOpen, setIsAddDocumentDrawerOpen] = useState(false)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    initialDocumentId ?? null
  )
  // Separate open flag; the id is kept on close so the view drawer can play
  // its exit animation (the next tap overwrites it).
  const [isViewDocumentOpen, setIsViewDocumentOpen] = useState(!!initialDocumentId)
  const { isAdmin, isLoaded } = useIsAdmin()
  const { data: currentCrew } = useCurrentCrewMember()
  const findOrCreateConversation = useFindOrCreateConversation()
  const resendInvite = useCrewResendInvite()
  const isDesktop = useIsDesktop()

  const handleMessageClick = async () => {
    if (!crew?.id || !currentCrew?.companyId || !currentCrew?.id) return
    const conversationId = await findOrCreateConversation({
      companyId: currentCrew.companyId,
      otherCrewId: crew.id,
      crewId: currentCrew.id,
    })
    navigate({ to: `/conversations/${conversationId}` })
  }

  const handleResendInvite = () => {
    resendInvite.mutate(
      { crewId },
      {
        onSuccess: () => {
          toast.success('Invitation email sent')
        },
        onError: () => {
          toast.error('Failed to send invitation email')
        },
      }
    )
  }

  if (isLoading) {
    // Mirrors the PAXDetailView loading state
    const loadingBody = (
      <>
        {/* Profile Card */}
        <Card className="flex items-center justify-center px-6 py-4">
          <div className="flex flex-col items-center gap-3">
            <Skeleton className="size-18 rounded-full" />
            <Skeleton className="h-5 w-32" />
          </div>
        </Card>

        {/* Info Cards */}
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={i} className="px-4 py-4">
              <div className="flex flex-col gap-4">
                <div className="flex gap-2 items-center">
                  <Skeleton className="size-8 rounded-full shrink-0" />
                  <Skeleton className="h-4 w-32" />
                </div>
                <Skeleton className="h-3 w-3/4" />
              </div>
            </Card>
          ))}
        </div>
      </>
    )

    if (isDesktop) {
      return (
        <DesktopPage>
          <DesktopPageHeader title="Crew" />
          {loadingBody}
        </DesktopPage>
      )
    }

    return (
      <AppLayout
        topBar={<TopBarBackIconButton title="Crew" />}
        className="min-h-0 pt-6 pb-[calc(var(--safe-bottom)+32px)]"
      >
        {loadingBody}
      </AppLayout>
    )
  }

  // Group persisted preference photos by preference key for the read-only grids.
  const photosByPreference = (
    (crew as { preferencePhotos?: { preference: string; s3Key: string }[] } | undefined)
      ?.preferencePhotos ?? []
  ).reduce<Record<string, string[]>>((acc, p) => {
    acc[p.preference] = [...(acc[p.preference] ?? []), p.s3Key];
    return acc;
  }, {})

  const preferencePhotoGrid = (preference: string) => {
    const s3Keys = photosByPreference[preference] ?? []
    if (s3Keys.length === 0) return undefined
    return <CrewPreferencePhotos readOnly preference={preference} s3Keys={s3Keys} />
  }

  const crewName = `${crew?.firstName ?? ''} ${crew?.lastName ?? ''}`.trim()
  // Breadcrumb follows the entry point: the operations route gets the full
  // Operations / Crew trail, the crew-facing directory route just Crew.
  const crewBreadcrumb = (
    <span className="flex items-center gap-2">
      <button
        type="button"
        onClick={handleBack}
        className="flex size-7 items-center justify-center rounded-full hover:bg-surface-L1"
        aria-label="Back to crew directory"
      >
        <ArrowLeft className="size-4" />
      </button>
      <span>
        {backTo === '/operations/crew-directory' && (
          <>
            <Link to="/operations" className="hover:underline">
              Operations
            </Link>
            <span className="text-content-primary-subtle"> / </span>
          </>
        )}
        <Link to={backTo} className="hover:underline">
          Crew
        </Link>
        <span className="text-content-primary-subtle"> / {crewName}</span>
      </span>
    </span>
  )

  const visibleDocuments = publicView
    ? crew?.documents?.filter((doc) => doc.isPublic) ?? []
    : crew?.documents ?? []

  // Basic view for non-admin users
  if (!isAdmin) {
    const basicBody = (
      <>
        {/* Profile Card */}
        <Card className="flex gap-8 items-center px-6 py-4">
          {/* Left - Avatar and Name */}
          <div className="flex-1 flex flex-col items-center gap-3">
            <div className="relative">
              <ProfileAvatar
                className="size-18"
                photoS3Key={crew.photoS3Key}
                avatarColor={crew.avatarColor}
                initials={`${crew.firstName[0]}${crew.lastName[0]}`}
                fallbackClassName="text-[35px] font-semibold uppercase text-background"
              />
              {crew.isAdmin && (
                <div className="absolute -top-1 left-12 bg-background-brand-bold rounded-full p-1">
                  <Crown className="text-white size-3.5" />
                </div>
              )}
            </div>
            <p className="text-lg font-semibold">{crew.firstName}</p>
          </div>

          {/* Right - Flight Stats */}
          <CrewFlightStats crew={crew} />
        </Card>

        {/* Row Roster / Message */}
        <div className="flex gap-2 mt-4">
          <Card
            className="flex-1 flex flex-col items-center justify-center gap-1 py-4 px-4 cursor-pointer hover:bg-surface-L2 transition-colors"
            onClick={() => navigate({ to: '/roster/$crewId', params: { crewId } } as any)}
          >
            <CalendarDays className="size-4 text-content-brand-bold" />
            <p className="text-xs font-medium text-center">Roster</p>
          </Card>
          <Card
            className="flex-1 flex flex-col items-center justify-center gap-1 py-4 px-4 cursor-pointer hover:bg-surface-L2 transition-colors"
            onClick={handleMessageClick}
          >
            <MessageSquare className="size-4 text-content-brand-bold" />
            <p className="text-xs font-medium text-center">Message</p>
          </Card>
        </div>

        {/* Basic Info Card */}
        <Card className="mt-4 px-4 py-1">
          <PrntrGroup>
            <PrntrInput label="Name">
              <PrntrValue value={crew.name} placeholder="—" />
            </PrntrInput>
            <PrntrInput label="Nationality">
              <PrntrValue value={crew.nationality || null} placeholder="—" />
            </PrntrInput>
            <PrntrInput label="Base">
              <PrntrValue
                value={(crew.baseAirportIcao && getAirportByIcao(crew.baseAirportIcao)?.name) || null}
                placeholder="—"
              />
            </PrntrInput>
            <PrntrInput label="Telephone">
              <PhoneLink phone={crew.phone} phoneCountry={crew.phoneCountry} />
            </PrntrInput>
            <PrntrInput label="Email">
              <EmailLink email={crew.email} />
            </PrntrInput>
            <PrntrInput label="Crew Role">
              <PrntrValue value={formatCrewRole(crew.crewRole) || null} placeholder="—" />
            </PrntrInput>
            {isPilotRole(crew.crewRole) && (
              <PrntrInput label="Pilot License No.">
                <PrntrValue value={crew.pilotLicenseNumber || null} placeholder="—" />
              </PrntrInput>
            )}
          </PrntrGroup>
        </Card>

        <div className="flex flex-col gap-4 mt-4">
          {/* Medical Information Card */}
          <PreferenceCard
            icon={<Stethoscope className="size-4 text-content-brand-bold" />}
            title="Medical Information"
            value={crew.medicalInfo || '-'}
            photos={preferencePhotoGrid('medicalInfo')}
          />

          {/* Food Preferences Card */}
          <PreferenceCard
            icon={<Soup className="size-4 text-content-brand-bold" />}
            title="Food Preferences"
            value={crew.foodPreferences || '-'}
            photos={preferencePhotoGrid('foodPreferences')}
          />

          {/* Beverage Preferences Card */}
          <PreferenceCard
            icon={<CupSoda className="size-4 text-content-brand-bold" />}
            title="Beverage Preferences"
            value={crew.beveragePreferences || '-'}
            photos={preferencePhotoGrid('beveragePreferences')}
          />
        </div>

        {/* Public Documents */}
        {(crewDocs?.length ?? 0) > 0 && (
          <div className="flex flex-col gap-3 mt-4">
            <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[1.5px]">
              <span className="text-content-primary-subtle-mid">Documents</span>
            </div>
            {crewDocs!.map((doc) => {
              const DocumentIcon = getDocumentIcon(doc.subType)
              return (
                <DocumentListCard
                  key={doc.id}
                  icon={<DocumentIcon />}
                  title={doc.title}
                  subTypeLabel={documentSubTypeLabels[doc.subType] || doc.subType}
                  expiryLabel={
                    doc.expiryDate
                      ? `Expires ${new Date(doc.expiryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                      : undefined
                  }
                  expiryStatus={
                    doc.expiryDate ? getDocumentExpiryStatus(doc.expiryDate) : undefined
                  }
                  isPublic={doc.isPublic}
                  onClick={() => {
                    setSelectedDocumentId(doc.id)
                    setIsViewDocumentOpen(true)
                  }}
                />
              )
            })}
          </div>
        )}

        <ViewDocumentDrawer
          isOpen={isViewDocumentOpen}
          onClose={() => setIsViewDocumentOpen(false)}
          documentId={selectedDocumentId}
          readOnly
        />
      </>
    )

    if (isDesktop) {
      return (
        <DesktopPage>
          <DesktopPageHeader title={crewName} breadcrumb={crewBreadcrumb} />
          {basicBody}
        </DesktopPage>
      )
    }

    return (
      <AppLayout
        topBar={<TopBarBackIconButton title='Crew'/>}
        className="min-h-0 pt-6 pb-[calc(var(--safe-bottom)+32px)]"
      >
        {basicBody}
      </AppLayout>
    )
  }

  // Full view for admin users
  const adminBody = (
    <>
      {/* Deleted Banner */}
      {crew.isDeleted && (
        <div className="bg-content-negative-bold/10 border border-border-negative-bold/20 rounded-lg px-4 py-2 mb-4">
          <p className="text-xs font-medium text-content-negative-bold text-center">This crew member has been deleted</p>
        </div>
      )}

      {/* Profile Card */}
        <Card className={`flex gap-8 items-center px-6 py-4 ${crew.isDeleted ? 'opacity-60' : ''}`}>
          {/* Left - Avatar and Name */}
          <div className="flex-1 flex flex-col items-center gap-3">
            <div className="relative">
              <ProfileAvatar
                className="size-18"
                photoS3Key={crew.photoS3Key}
                avatarColor={crew.avatarColor}
                initials={`${crew.firstName[0]}${crew.lastName[0]}`}
                fallbackClassName="text-[35px] font-semibold uppercase text-background"
              />
              {crew.isAdmin && (
                <div className="absolute -top-1 left-12 bg-background-brand-bold rounded-full p-1">
                  <Crown className="text-white size-3.5" />
                </div>
              )}
            </div>
            <p className="text-lg font-semibold">{crew.firstName} {crew.lastName}</p>
          </div>

          {/* Right - Flight Stats */}
          <CrewFlightStats crew={crew} />
        </Card>

        {/* Row Roster / Message / Invite */}

        <div className="flex gap-2 mt-4">
          <Card
            interactive
            className="flex-1 flex flex-col items-center justify-center gap-1 py-4 px-4 cursor-pointer hover:bg-surface-L2 transition-colors"
            onClick={() => navigate({ to: '/roster/$crewId', params: { crewId } } as any)}
          >
            <CalendarDays className="size-4 text-content-brand-bold" />
            <p className="text-xs font-medium text-center">Roster</p>
          </Card>
          <Card
            interactive
            className="flex-1 flex flex-col items-center justify-center gap-1 py-4 px-4 cursor-pointer hover:bg-surface-L2 transition-colors"
            onClick={handleMessageClick}
          >
            <MessageSquare className="size-4 text-content-brand-bold" />
            <p className="text-xs font-medium text-center">Message</p>
          </Card>
          <Card
            interactive
            className="flex-1 flex flex-col items-center justify-center gap-1 py-4 px-4 cursor-pointer hover:bg-surface-L2 transition-colors"
            onClick={handleResendInvite}
          >
            <Send
              className={`size-4 text-content-brand-bold ${resendInvite.isPending ? 'animate-pulse' : ''}`}
            />
            <p className="text-xs font-medium text-center">
              {resendInvite.isPending ? 'Sending...' : 'Resend Invite'}
            </p>
          </Card>
        </div>

        {/* Tabs - Details & Documents */}
        <Tabs value={initialTab} onValueChange={(value) => navigate({ search: { tab: value } as any, replace: true })} className="mt-4">
          <TabsList className="w-full">
            <TabsTrigger value="overview">
              Details
            </TabsTrigger>
            <TabsTrigger
              value="documents"
              className="flex items-center gap-1"
            >
              Documents
              {getDocumentsExpiryStatus(visibleDocuments) === 'expired' && (
                <TriangleAlert className="size-4 text-content-negative-bold" />
              )}
              {getDocumentsExpiryStatus(visibleDocuments) === 'expiring' && (
                <TriangleAlert className="size-4 text-background-notice-bold" />
              )}
            </TabsTrigger>
            <TabsTrigger value="flights">
              Flights
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="flex flex-col gap-4 mt-4">
            {/* Basic Info Card */}
            <Card className="px-4 py-1">
              <PrntrGroup>
                <PrntrInput label="Name">
                  <PrntrValue value={crew.name} placeholder="—" />
                </PrntrInput>
                <PrntrInput label="Nationality">
                  <PrntrValue value={crew.nationality || null} placeholder="—" />
                </PrntrInput>
                <PrntrInput label="Base">
                  <PrntrValue
                    value={(crew.baseAirportIcao && getAirportByIcao(crew.baseAirportIcao)?.name) || null}
                    placeholder="—"
                  />
                </PrntrInput>
                <PrntrInput label="Telephone">
                  <PhoneLink phone={crew.phone} phoneCountry={crew.phoneCountry} />
                </PrntrInput>
                <PrntrInput label="Email">
                  <EmailLink email={crew.email} />
                </PrntrInput>
                <PrntrInput label="Date of Birth">
                  <PrntrValue value={crew.dateOfBirth || null} placeholder="—" />
                </PrntrInput>
                <PrntrInput label="Crew Role">
                  <PrntrValue value={formatCrewRole(crew.crewRole) || null} placeholder="—" />
                </PrntrInput>
                {isPilotRole(crew.crewRole) && (
                  <PrntrInput label="Pilot License No.">
                    <PrntrValue value={crew.pilotLicenseNumber || null} placeholder="—" />
                  </PrntrInput>
                )}
                <PrntrInput label="Admin">
                  <YesNoBadge value={crew.isAdmin ?? false} />
                </PrntrInput>
                <PrntrInput label="Owner">
                  <YesNoBadge value={crew.isOwner ?? false} />
                </PrntrInput>
                <PrntrInput label="Included in schedule">
                  <YesNoBadge value={crew.includeInSchedule !== false} />
                </PrntrInput>
              </PrntrGroup>
            </Card>

            {/* Medical Information Card */}
            <PreferenceCard
              icon={<Stethoscope className="size-4 text-content-brand-bold" />}
              title="Medical Information"
              value={crew.medicalInfo || '-'}
              photos={preferencePhotoGrid('medicalInfo')}
            />

            {/* Food Preferences Card */}
            <PreferenceCard
              icon={<Soup className="size-4 text-content-brand-bold" />}
              title="Food Preferences"
              value={crew.foodPreferences || '-'}
              photos={preferencePhotoGrid('foodPreferences')}
            />

            {/* Beverage Preferences Card */}
            <PreferenceCard
              icon={<CupSoda className="size-4 text-content-brand-bold" />}
              title="Beverage Preferences"
              value={crew.beveragePreferences || '-'}
              photos={preferencePhotoGrid('beveragePreferences')}
            />

          </TabsContent>

          <TabsContent value="documents" className="flex flex-col gap-3 mt-4">
            {visibleDocuments.length > 0 ? (
              <>
                {visibleDocuments.map((doc) => {
                  const DocumentIcon = getDocumentIcon(doc.subType)
                  const expiryStatus = getDocumentExpiryStatus(doc.expiryDate)
                  const expiryLabel = doc.expiryDate
                    ? `Expires ${new Date(doc.expiryDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                    : 'Expiry date not set'
                  const subTypeLabel = documentSubTypeLabels[doc.subType] || doc.subType
                  const showAlert = doc.expiryDate && (expiryStatus === 'expired' || expiryStatus === 'expiring')
                  return (
                    <Card
                      key={doc.id}
                      interactive
                      className="flex items-center justify-between gap-4 cursor-pointer hover:bg-surface-L2 transition-colors"
                      onClick={() => {
                    setSelectedDocumentId(doc.id)
                    setIsViewDocumentOpen(true)
                  }}
                    >
                      <div className="flex gap-4 items-center min-w-0">
                        <DocumentIcon className="size-6 text-content-brand-bold shrink-0" />
                        <div className="flex flex-col gap-2 items-start min-w-0">
                          <p className="text-base font-semibold leading-6 tracking-[-0.5px] text-content-primary-bold truncate">
                            {doc.title}
                          </p>
                          <div className="flex flex-wrap gap-3 items-start">
                            <Badge
                              variant="outline"
                              className="border-border-L1 text-content-primary-subtle font-semibold py-[3px] min-h-6"
                            >
                              {subTypeLabel}
                            </Badge>
                            <Badge
                              variant="outline"
                              className="border-border-L1 text-content-primary-subtle font-semibold py-[3px] min-h-6"
                            >
                              {expiryLabel}
                            </Badge>
                            <Badge
                              variant="outline"
                              className="border-border-L1 text-content-primary-subtle font-semibold py-[3px] min-h-6"
                            >
                              {doc.isPublic ? 'Public' : 'Private'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      {showAlert && (
                        <AlertBadge
                          variant={expiryStatus === 'expired' ? 'error' : 'warning'}
                        />
                      )}
                    </Card>
                  )
                })}
                {!publicView && (
                  <Button
                    className="w-full mt-2"
                    onClick={() => {
                      hapticLight()
                      setIsAddDocumentDrawerOpen(true)
                    }}
                  >
                    Add Document
                  </Button>
                )}
              </>
            ) : (
              <EmptyStateCard
                label={publicView ? 'No documents yet' : 'Add a document'}
                onAdd={publicView ? undefined : () => {
                  hapticLight()
                  setIsAddDocumentDrawerOpen(true)
                }}
              />
            )}
          </TabsContent>

          <TabsContent value="flights" className="mt-4">
            <Card className="p-3">
              <FlightList
                compact
                hideCrewPax
                tripLinkTo="/schedule/$tripId"
                flights={
                  (crew?.tripLegs ?? [])
                    .filter((leg) => dayjs(leg.arrivalDateTime).isBefore(dayjs()))
                    .map((leg) => ({
                      id: leg.id,
                      tripId: leg.tripId,
                      departureAirportIcao: leg.departureAirportIcao,
                      arrivalAirportIcao: leg.arrivalAirportIcao,
                      departureDateTime: leg.departureDateTime,
                      arrivalDateTime: leg.arrivalDateTime,
                      aircraftRegistration: leg.aircraft?.registration,
                    }))
                }
                emptyMessage="No flight history"
              />
            </Card>
          </TabsContent>
        </Tabs>

      <DocumentFormDrawer
        isOpen={isAddDocumentDrawerOpen}
        owner={{ type: 'crew', crewId }}
        onClose={() => setIsAddDocumentDrawerOpen(false)}
      />

      {/* View Document Drawer */}
      <ViewDocumentDrawer
        isOpen={isViewDocumentOpen}
        onClose={() => setIsViewDocumentOpen(false)}
        documentId={selectedDocumentId}
      />
    </>
  )

  if (isDesktop) {
    return (
      <DesktopPage>
        <DesktopPageHeader
          title={crewName}
          breadcrumb={crewBreadcrumb}
          action={<CrewActionsSelect crewId={crewId} onCrewDeleted={() => {}} />}
        />
        {adminBody}
      </DesktopPage>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          title='Crew'
          rightElement={<CrewActionsSelect crewId={crewId} onCrewDeleted={() => {}} />}
        />
      }
      className="min-h-0 pt-6 pb-[calc(var(--safe-bottom)+32px)]"
    >
      {adminBody}
    </AppLayout>
  )
}
